import { useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Paper,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  WarningAmber as WarningIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import {
  OutreachTemplate,
  OutreachTemplateCategory,
  outreachTemplatesApi,
} from '../../services/api'
import DialogShell from '../common/DialogShell'
import { useNotification } from '../../hooks/useNotification'
import { formatPhoneNumber, toE164 } from '../../utils/formatPhone'
import { PRE_OUTREACH_STAGE } from '../../constants/networking'
import type { OutreachRecipient } from '../../utils/outreachRecipient'

// Standard 1-segment GSM-7 SMS = 160 chars. Anything longer still sends via
// the user's local messaging app fine, just gets split into multiple paid
// segments by their carrier. We surface a live counter so the user knows.
const SMS_SEGMENT_LIMIT = 160

// Browsers cap mailto: URIs at ~2000 chars (some clients/mailto handlers
// even lower). If the resolved body approaches this, the user's mail app
// may silently truncate. Show a warning when we're close.
const MAILTO_BODY_WARN_THRESHOLD = 1800

// Best-effort guess at "is this a mobile device" for the SMS handoff hint.
// `sms:` deeplinks work reliably on iOS / Android and unreliably on desktop.
// We default to showing the disclosure on every device (cheap), and elevate
// the warning when we're confident the user is on desktop.
function looksLikeDesktop(): boolean {
  if (typeof navigator === 'undefined') return false
  const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0
  const ua = navigator.userAgent || ''
  const mobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
  return !hasTouch && !mobileUA
}

const MERGE_FIELD_META: Record<string, { label: string; fallback: string; guidance: string }> = {
  contactName: {
    label: 'Contact Name',
    fallback: '',
    guidance: 'This contact has no name set.',
  },
  company: {
    label: 'Company',
    fallback: '',
    guidance: 'Add a company to this contact\'s profile.',
  },
  sharedConnection: {
    label: 'Shared Connection',
    fallback: 'a mutual connection',
    guidance: 'Add a referral source on this contact\'s detail page.',
  },
  // MWA-310 — the questions a search partner wants answered up front. Listed
  // here so the existing empty-field warning catches an unanswered one before
  // the intro goes out. A networking template simply never uses them.
  clientCity: {
    label: 'Client Location',
    fallback: '',
    guidance: 'Add location preferences in the Dream Job section.',
  },
  compensationRange: {
    label: 'Compensation Range',
    fallback: '',
    guidance: 'Set a salary target or minimum in the Dream Job section.',
  },
  relocation: {
    label: 'Relocation',
    fallback: '',
    guidance: 'Set relocation willingness in the Dream Job section.',
  },
  targetTitles: {
    label: 'Target Titles',
    fallback: '',
    guidance: 'Set a target role in the Dream Job section.',
  },
  industries: {
    label: 'Industries',
    fallback: '',
    guidance: 'Set a target industry in the Dream Job section.',
  },
  roleTitle: {
    label: 'Role Title',
    fallback: '',
    guidance: 'Set your target role in the Dream Job section of your profile.',
  },
}

interface OutreachComposerProps {
  /**
   * Who the message is for. MWA-310 — a networking Contact and a search-partner
   * Recruiter both adapt to this shape (see utils/outreachRecipient), so one
   * composer serves both rather than two that drift apart.
   */
  recipient: OutreachRecipient
  /**
   * Records the outreach once it has gone out. Contacts write a contact
   * interaction; search partners mark the recruiter contacted, because the
   * interaction record is keyed by contactId or interviewId and a recruiter is
   * neither.
   */
  onRecordOutreach: (entry: {
    templateId: string
    resolvedBody: string
    channel: 'linkedin' | 'email' | 'sms'
  }) => Promise<{ undo: () => Promise<void> }>
  /**
   * Whether SMS is a channel for this recipient. Off for search partners — the
   * intro is a form letter, not a text. Deliberately NOT derived from an empty
   * phone: see the Send SMS button below for why that would be wrong.
   */
  /**
   * Domain-specific merge values the caller knows and the composer does not,
   * such as the search-partner answers drawn from the client Dream Job.
   */
  extraMergeValues?: Record<string, string>
  /**
   * Which template categories this surface offers, by slug. Each caller says
   * what it means rather than the composer guessing: networking must not
   * offer a search-partner form letter, and the search-partner surface must
   * not offer networking templates. Defaults to every category.
   */
  categoryFilter?: (slug: string) => boolean
  /**
   * Stack the template list above the editor instead of beside it.
   *
   * The responsive default keys off the VIEWPORT, which is wrong for a host
   * that is itself narrow: inside the 440px recruiter drawer on a wide screen
   * it still took the side-by-side branch and squeezed the message box to
   * 146px — narrower than the same editor gets on a phone.
   */
  stacked?: boolean
  allowSms?: boolean
  clientTargetRole?: string
  onOutreachSent?: () => void
  /**
   * Called once an outreach has been recorded. This used to close a dialog
   * stacked on top of the contact panel; inline it returns the panel to the
   * record view, where the interaction the user just logged is visible in
   * Activity.
   */
  onDone?: () => void
}

function resolveMergeFields(body: string, values: Record<string, string>): string {
  return body.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? `{${key}}`)
}

function getEmptyMergeFields(templateBody: string, values: Record<string, string>): string[] {
  const fieldRegex = /\{(\w+)\}/g
  const usedFields = new Set<string>()
  let match
  while ((match = fieldRegex.exec(templateBody)) !== null) {
    usedFields.add(match[1])
  }
  return [...usedFields].filter((key) => key in MERGE_FIELD_META && !values[key])
}

export default function OutreachComposer({ recipient, onRecordOutreach, extraMergeValues, categoryFilter, stacked = false, allowSms = true, clientTargetRole, onOutreachSent, onDone }: OutreachComposerProps) {
  const [categories, setCategories] = useState<OutreachTemplateCategory[]>([])
  const [templates, setTemplates] = useState<OutreachTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [selectedTemplate, setSelectedTemplate] = useState<OutreachTemplate | null>(null)
  const [editedBody, setEditedBody] = useState('')
  const [checkedPoints, setCheckedPoints] = useState<Record<number, boolean>>({})
  const [sending, setSending] = useState(false)
  const [copied, setCopied] = useState(false)
  const [emptyFieldWarningOpen, setEmptyFieldWarningOpen] = useState(false)
  const [pendingTemplate, setPendingTemplate] = useState<OutreachTemplate | null>(null)
  const [emptyFields, setEmptyFields] = useState<string[]>([])
  // Send modal state — Send Email and Send SMS each open a small confirmation
  // modal where the user can review/edit the resolved fields before we open
  // their default mail or messaging app via mailto: / sms:. Nested inside the
  // outer dialog so closing them doesn't drop the template selection.
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [smsModalOpen, setSmsModalOpen] = useState(false)
  const [smsTo, setSmsTo] = useState('')
  const [smsBody, setSmsBody] = useState('')
  const isDesktop = looksLikeDesktop()
  const { showSuccess, showError, showUndoNotification } = useNotification()

  // Keyed on the contact rather than on an open flag: the panel mounts this
  // view only while it is showing, so mounting is what "opened" used to mean,
  // and selecting a different contact must clear the half-composed message.
  useEffect(() => {
    fetchTemplates()
    setSelectedTemplate(null)
    setEditedBody('')
    setCheckedPoints({})
    setActiveTab(0)
    setEmptyFieldWarningOpen(false)
    setPendingTemplate(null)
    setEmptyFields([])
  }, [recipient.id])

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const result = await outreachTemplatesApi.list()
      setCategories((result.categories || []).filter((c) => (categoryFilter ? categoryFilter(c.slug) : true)))
      setTemplates(result.templates || [])
    } catch (err) {
      console.error('Failed to load templates:', err)
      showError('Failed to load templates')
    }
    setLoading(false)
  }

  const buildMergeValues = (): Record<string, string> => {
    // Treat UUID-like referredBy values as empty (legacy data stored contact IDs instead of names)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    const referredBy = recipient.referredBy && !isUuid.test(recipient.referredBy) ? recipient.referredBy : ''
    return {
      contactName: recipient.displayName,
      company: recipient.company,
      sharedConnection: referredBy,
      roleTitle: clientTargetRole || '',
      ...extraMergeValues,
    }
  }

  const handleSelectTemplate = (template: OutreachTemplate) => {
    const mergeValues = buildMergeValues()
    const empty = getEmptyMergeFields(template.body, mergeValues)

    if (empty.length > 0) {
      setPendingTemplate(template)
      setEmptyFields(empty)
      setEmptyFieldWarningOpen(true)
      return
    }

    setSelectedTemplate(template)
    setEditedBody(resolveMergeFields(template.body, mergeValues))
    setCheckedPoints({})
  }

  const handleContinueAnyway = () => {
    if (!pendingTemplate) return
    const mergeValues = buildMergeValues()
    for (const key of emptyFields) {
      const meta = MERGE_FIELD_META[key]
      if (meta?.fallback) {
        mergeValues[key] = meta.fallback
      }
    }
    setSelectedTemplate(pendingTemplate)
    setEditedBody(resolveMergeFields(pendingTemplate.body, mergeValues))
    setCheckedPoints({})
    setEmptyFieldWarningOpen(false)
    setPendingTemplate(null)
    setEmptyFields([])
  }

  const handleGoBack = () => {
    setEmptyFieldWarningOpen(false)
    setPendingTemplate(null)
    setEmptyFields([])
  }

  // Copying and logging are one action. They were two buttons, and the copy
  // one recorded nothing — so the LinkedIn path, which is the common one, left
  // no trace unless the user remembered to press a second button afterwards.
  // The mailto and sms handoffs already logged on click; this brings the
  // copy-and-paste path in line with them.
  //
  // It does record an outreach the user has not pasted yet. Undo is what makes
  // that acceptable, so it is offered on the confirmation rather than left to
  // the user to ask for.
  const handleCopyAndLog = async () => {
    if (!selectedTemplate) return

    try {
      await navigator.clipboard.writeText(editedBody)
    } catch {
      // Nothing is logged if the copy failed — there is no outreach to record.
      showError('Failed to copy to clipboard')
      return
    }

    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    setSending(true)
    try {
      const recorded = await onRecordOutreach({
        templateId: selectedTemplate.id,
        resolvedBody: editedBody,
        channel: 'linkedin',
      })
      showUndoNotification('Copied, and logged as outreach', async () => {
        try {
          await recorded.undo()
          showSuccess('Outreach undone')
          onOutreachSent?.()
        } catch (undoErr) {
          console.error('Failed to undo outreach:', undoErr)
          showError('Could not undo that outreach')
        }
      })
      onOutreachSent?.()
      onDone?.()
    } catch (err) {
      console.error('Failed to log outreach:', err)
      // The text is on the clipboard either way, so say so rather than
      // implying the copy failed too.
      showError('Copied, but could not log the outreach')
    }
    setSending(false)
  }

  // Open the Send Email modal with the resolved fields prefilled. The actual
  // handoff happens when the user clicks "Open in Email App" inside the modal.
  const handleOpenEmailModal = () => {
    if (!selectedTemplate) return
    setEmailTo(recipient.email)
    setEmailSubject(selectedTemplate.title || '')
    setEmailBody(editedBody)
    setEmailModalOpen(true)
  }

  // Open the Send SMS modal with the resolved fields prefilled.
  const handleOpenSmsModal = () => {
    if (!selectedTemplate) return
    // Formatted for the field, which is a human-readable input. The sms:
    // handoff strips it back to digits.
    setSmsTo(formatPhoneNumber(recipient.phone))
    setSmsBody(editedBody)
    setSmsModalOpen(true)
  }

  // Hand off to the user's default mail client via mailto:. We log the
  // outreach as "sent" at the moment of the click — this is best-effort
  // (the user could close their mail app without sending), matching how
  // "I Sent This" used to work but with stronger intent signal.
  const handleSendEmailViaMailto = async () => {
    if (!selectedTemplate) return
    if (!emailTo.trim()) {
      showError('Recipient email is required')
      return
    }
    if (!emailBody.trim()) {
      showError('Message body is required')
      return
    }
    if (emailBody.length > MAILTO_BODY_WARN_THRESHOLD) {
      const proceed = window.confirm(
        'This message is long and your email app may truncate it when it opens. ' +
          'Consider copying to clipboard and pasting manually instead. Open mail app anyway?'
      )
      if (!proceed) return
    }
    const mailtoUrl = `mailto:${encodeURIComponent(emailTo.trim())}?subject=${encodeURIComponent(
      emailSubject
    )}&body=${encodeURIComponent(emailBody)}`
    // Open the user's default mail client. We use window.location.href
    // rather than window.open so we don't get a blocked-popup warning, and
    // because mailto: doesn't open in a real tab — it triggers a protocol
    // handoff. The current page stays put.
    window.location.href = mailtoUrl
    setSending(true)
    try {
      await onRecordOutreach({
        templateId: selectedTemplate.id,
        resolvedBody: emailBody,
        channel: 'email',
      })
      showSuccess('Email handed off to your mail app and logged')
      onOutreachSent?.()
      setEmailModalOpen(false)
      onDone?.()
    } catch (err) {
      console.error('Failed to log outreach after email handoff:', err)
      // Don't fail the user's send because logging failed; the mailto: has
      // already opened. Surface a softer warning so they can manually log
      // via the External Send button if needed.
      showError('Mail app opened, but failed to record the send. Use "Log External Send" if needed.')
      setEmailModalOpen(false)
    }
    setSending(false)
  }

  // Hand off to the user's default SMS app via sms:. Same best-effort
  // logging semantics as the email path.
  const handleSendSmsViaSmsUri = async () => {
    if (!selectedTemplate) return
    if (!smsTo.trim()) {
      showError('Recipient phone number is required')
      return
    }
    if (!smsBody.trim()) {
      showError('Message body is required')
      return
    }
    // sms: spec differs across platforms (iOS uses &body=, Android uses ;body=,
    // but most modern handlers accept ?body=). Stick with ?body= as the most
    // widely supported form.
    // Digits only: the field accepts a human-readable number, and an sms: URI
    // carrying brackets and dashes is not reliably dialled.
    const smsRecipient = toE164(smsTo.trim()) || smsTo.trim()
    const smsUrl = `sms:${encodeURIComponent(smsRecipient)}?body=${encodeURIComponent(smsBody)}`
    window.location.href = smsUrl
    setSending(true)
    try {
      await onRecordOutreach({
        templateId: selectedTemplate.id,
        resolvedBody: smsBody,
        channel: 'sms',
      })
      showSuccess('SMS handed off to your messaging app and logged')
      onOutreachSent?.()
      setSmsModalOpen(false)
      onDone?.()
    } catch (err) {
      console.error('Failed to log outreach after SMS handoff:', err)
      showError('Messaging app opened, but failed to record the send. Use "Log External Send" if needed.')
      setSmsModalOpen(false)
    }
    setSending(false)
  }

  const currentCategory = categories[activeTab]
  const filteredTemplates = currentCategory
    ? templates.filter((t) => {
        if (t.categoryId !== currentCategory.id) return false
        // Filter by contact status if template has assignedStages
        if (t.assignedStages && t.assignedStages.length > 0) {
          // An unactioned contact has no status, and its stage is the
          // pre-outreach one — the same fallback OutreachChecklist uses.
          return t.assignedStages.includes(recipient.stage ?? PRE_OUTREACH_STAGE)
        }
        return true
      })
    : []

  return (
    <>
      <Box>
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : categories.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No templates available. Ask your coach to set up outreach templates.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {/* Category tabs */}
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {categories.map((cat) => (
                  <Tab key={cat.id} label={cat.name} />
                ))}
              </Tabs>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: stacked ? 'column' : { xs: 'column', sm: 'row' },
                  gap: 2,
                  minHeight: 300,
                }}
              >
                {/* Template list */}
                <Paper
                  variant="outlined"
                  sx={{
                    width: stacked ? '100%' : { xs: '100%', sm: 240 },
                    maxHeight: stacked ? 200 : { xs: 200, sm: 'none' },
                    flexShrink: 0,
                    overflowY: 'auto',
                  }}
                >
                  {filteredTemplates.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      No templates in this category.
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {filteredTemplates.map((t) => (
                        <ListItemButton
                          key={t.id}
                          selected={selectedTemplate?.id === t.id}
                          onClick={() => handleSelectTemplate(t)}
                        >
                          <ListItemText
                            primary={t.title}
                            secondary={t.seniorityLevel || undefined}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Paper>

                {/* Template editor / preview */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {!selectedTemplate ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      Select a template to get started
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="subtitle2">{selectedTemplate.title}</Typography>

                      <TextField
                        value={editedBody}
                        onChange={(e) => setEditedBody(e.target.value)}
                        multiline
                        rows={6}
                        fullWidth
                        size="small"
                        helperText="Edit to personalize your message"
                      />

                      {/* Talking points */}
                      {selectedTemplate.talkingPoints && selectedTemplate.talkingPoints.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Talking Points
                          </Typography>
                          {selectedTemplate.talkingPoints.map((point, idx) => (
                            <FormControlLabel
                              key={idx}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!!checkedPoints[idx]}
                                  onChange={(e) => setCheckedPoints((prev) => ({ ...prev, [idx]: e.target.checked }))}
                                />
                              }
                              label={<Typography variant="body2">{point}</Typography>}
                              sx={{ display: 'flex', ml: 0 }}
                            />
                          ))}
                        </Paper>
                      )}

                      {/*
                        Action bar. Four categories of action:
                        - Send Email / Send SMS: hand off to the user's own
                          mail / messaging app via mailto:/sms:. The send
                          happens FROM the user's configured identity in
                          their local app, not from any MissFit credential.
                        - Copy & Log: the LinkedIn path. Copies the text and
                          records the outreach in one action, because doing
                          them separately meant the most common channel left
                          no trace. Offers an Undo on the confirmation.
                        - Open LinkedIn: the other half of that path. The
                          message is written here but sent over there, and the
                          CSV import already carries the profile URL, so there
                          is no reason to make them search LinkedIn by hand.
                      */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Tooltip
                          title={
                            !recipient.email
                              ? 'Add an email to this contact to enable Send Email'
                              : ''
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              startIcon={<EmailIcon />}
                              onClick={handleOpenEmailModal}
                              disabled={sending || !editedBody.trim() || !recipient.email}
                              size="small"
                            >
                              Send Email
                            </Button>
                          </span>
                        </Tooltip>
                        {/*
                          Send SMS is NOT gated on recipient.phone. It is now a
                          real, writable field — Add Contact collects it and the
                          contact panel edits it — but it stays optional, so
                          gating on it would disable the button for every
                          contact whose number nobody has recorded yet. The
                          recipient is prefilled from it when there is one and
                          typed into the SMS modal when there is not.

                          MWA-310 — `allowSms` is a separate question from
                          whether a number is known: a search-partner intro is
                          a form letter, so the channel does not apply at all.
                        */}
                        {allowSms && (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SmsIcon />}
                            onClick={handleOpenSmsModal}
                            disabled={sending || !editedBody.trim()}
                            size="small"
                          >
                            Send SMS
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={<CopyIcon />}
                          onClick={handleCopyAndLog}
                          disabled={sending || !editedBody.trim()}
                          size="small"
                        >
                          {copied ? 'Copied!' : sending ? 'Logging...' : 'Copy & Log'}
                        </Button>
                        {recipient.linkedinUrl && (
                          <Button
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            href={recipient.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            Open LinkedIn
                          </Button>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
        )}
      </Box>

      {/*
        Send Email modal — review/edit prefilled fields, then hand off to the
        user's default mail app via mailto:. We never see the actual sent
        email; it goes from the user's configured email account to the
        recipient. We just log the outreach record on click.
      */}
      <DialogShell
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        maxWidth="sm"
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Send via your email
          </Box>
        }
        footer={
          <>
            <Button onClick={() => setEmailModalOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={handleSendEmailViaMailto}
              disabled={sending || !emailTo.trim() || !emailBody.trim()}
            >
              Open in Email App
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" variant="outlined">
            This will open your default email app with these fields prefilled.
            Review the message there and click Send inside your email app.
            The email will be sent from your own email account.
          </Alert>
          <TextField
            label="To"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Message"
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            multiline
            rows={8}
            size="small"
            fullWidth
            helperText={
              emailBody.length > MAILTO_BODY_WARN_THRESHOLD
                ? `Long message (${emailBody.length} chars). Some email apps may truncate when handed off — consider Copy to Clipboard instead.`
                : `${emailBody.length} characters`
            }
            error={emailBody.length > MAILTO_BODY_WARN_THRESHOLD}
          />
        </Box>
      </DialogShell>

      {/*
        Send SMS modal — same shape as the email modal but hands off to the
        sms: protocol. sms: behavior varies by platform: iOS/Android open
        Messages reliably, macOS opens Messages if the device is iCloud-paired,
        Windows often has nothing to handle it. We surface a "best on mobile"
        disclosure either way so the user isn't surprised.
      */}
      <DialogShell
        open={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        maxWidth="sm"
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <SmsIcon color="secondary" />
            Send via your phone
          </Box>
        }
        footer={
          <>
            <Button onClick={() => setSmsModalOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<OpenInNewIcon />}
              onClick={handleSendSmsViaSmsUri}
              disabled={sending || !smsTo.trim() || !smsBody.trim()}
            >
              Open in Messages App
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" variant="outlined">
            This will open your default messaging app with the message prefilled.
            Review and tap Send inside your messages app. The SMS will be sent from
            your own phone number — best used on a mobile device.
            {isDesktop && ' On desktop your browser may not be able to open a messages app at all.'}
          </Alert>
          <TextField
            label="To (phone number)"
            value={smsTo}
            onChange={(e) => setSmsTo(e.target.value)}
            size="small"
            fullWidth
            placeholder="+1 555 555 1234"
          />
          <TextField
            label="Message"
            value={smsBody}
            onChange={(e) => setSmsBody(e.target.value)}
            multiline
            rows={5}
            size="small"
            fullWidth
            helperText={(() => {
              const len = smsBody.length
              const segments = Math.max(1, Math.ceil(len / SMS_SEGMENT_LIMIT))
              if (len <= SMS_SEGMENT_LIMIT) {
                return `${len} / ${SMS_SEGMENT_LIMIT} characters — 1 segment`
              }
              return `${len} characters — ${segments} segments (your carrier will bill multiple messages)`
            })()}
          />
        </Box>
      </DialogShell>

      {/* Empty merge fields warning dialog */}
      <DialogShell
        open={emptyFieldWarningOpen}
        onClose={handleGoBack}
        compact
        maxWidth="sm"
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Some fields couldn't be fully personalized
          </Box>
        }
        footer={
          <>
            <Button onClick={handleGoBack}>Go Back</Button>
            <Button onClick={handleContinueAnyway} variant="contained">Continue Anyway</Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {emptyFields.map((key) => {
            const meta = MERGE_FIELD_META[key]
            if (!meta) return null
            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  p: 1.25,
                  border: 1,
                  borderColor: 'warning.main',
                  borderRadius: 1,
                }}
              >
                <WarningIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{meta.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{meta.guidance}</Typography>
                </Box>
              </Box>
            )
          })}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          You can continue and edit the message manually, or fill in the missing details first for automatic personalization.
        </Typography>
      </DialogShell>
    </>
  )
}
